import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Gauge, Bell, Users } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import { dashboardApi, leaseApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import type { BuildingMini, Lease, OrgDashboard } from "@/types";
import { formatDate, formatNumber } from "@/utils/format";

export default function OrgDashboardPage() {
  const user = useAuthStore((s) => s.user)!;
  const [data, setData] = useState<OrgDashboard | null>(null);
  const [expiring, setExpiring] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user.organisation_id) return;
    const orgId = user.organisation_id;
    dashboardApi.org(orgId).then((d) => {
      setData(d);
      setLoading(false);
    });
    leaseApi.expiring(orgId, 60).then(setExpiring).catch(() => setExpiring([]));
  }, [user.organisation_id]);

  if (!user.organisation_id) {
    return (
      <AppLayout>
        <p className="text-sm text-slate-500">No organisation associated with this account.</p>
      </AppLayout>
    );
  }

  const columns: Column<BuildingMini>[] = [
    { key: "name", header: "Building", render: (b) => (
        <Link to={`/buildings/${b.id}`} className="text-brand-700 font-medium hover:underline">{b.name}</Link>
      ) },
    { key: "units", header: "Units", render: (b) => b.unit_count },
    { key: "meters", header: "Meters", render: (b) => b.meter_count },
    { key: "alerts", header: "Open alerts", render: (b) => (
        b.open_alert_count > 0 ? (
          <span className="badge bg-red-50 text-red-700">{b.open_alert_count}</span>
        ) : <span className="text-slate-400">0</span>
      ) },
  ];

  const leaseColumns: Column<Lease>[] = [
    { key: "tenant", header: "Tenant", render: (l) => (
        <Link to={`/buildings/${l.building_id}/units/${l.unit_id}`} className="text-brand-700 font-medium hover:underline">
          {l.tenant_name}
        </Link>
      ) },
    { key: "unit", header: "Unit", render: (l) => l.unit_number ?? "—" },
    { key: "building", header: "Building", render: (l) => l.building_name ?? "—" },
    { key: "end", header: "Lease end", render: (l) => formatDate(l.lease_end_date) },
    { key: "days", header: "Days left", render: (l) => {
        const d = l.days_to_expiry ?? 0;
        const cls = d < 0 ? "bg-red-50 text-red-800" : d <= 14 ? "bg-red-50 text-red-800" : d <= 30 ? "bg-amber-50 text-amber-800" : "bg-yellow-50 text-yellow-800";
        return <span className={`badge ${cls}`}>{d < 0 ? "Expired" : `${d}d`}</span>;
      } },
  ];

  return (
    <AppLayout>
      <PageHeader title="Portfolio overview" subtitle="Consumption across your buildings, last 6 months" />
      {loading || !data ? (
        <LoadingState />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Buildings" value={data.building_count} icon={<Building2 size={18} />} to="/buildings" />
            <KpiCard label="Units" value={data.unit_count} icon={<Users size={18} />} accent="blue" to="/buildings" />
            <KpiCard label="Meters" value={data.meter_count} icon={<Gauge size={18} />} accent="slate" to="/buildings" />
            <KpiCard
              label="Open alerts"
              value={data.unresolved_alert_count}
              icon={<Bell size={18} />}
              accent={data.unresolved_alert_count ? "red" : "brand"}
              to="/alerts"
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="card p-5">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Electricity (kWh)</h2>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    formatter={(v: number) => formatNumber(v, 0)}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Line type="monotone" dataKey="electricity_kwh" stroke="#0F766E" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="card p-5">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Water (kL)</h2>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    formatter={(v: number) => formatNumber(v, 1)}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Line type="monotone" dataKey="water_kl" stroke="#0369a1" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          {expiring.length > 0 && (
            <div className="card p-5 mb-6">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Leases expiring within 60 days</h2>
              <DataTable
                columns={leaseColumns}
                rows={expiring}
                emptyMessage="No leases expiring soon"
              />
            </div>
          )}
          <DataTable columns={columns} rows={data.buildings} emptyMessage="No buildings yet" />
        </>
      )}
    </AppLayout>
  );
}
